'use client';

import { Link } from 'react-router';
import {
  Copy,
  CreditCard,
  Heart,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Users,
  Zap,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { SubscriptionOrgTheme } from './SubscriptionStoreTheme';

const PERKS = [
  { icon: Users, label: 'Comunidad exclusiva' },
  { icon: Zap, label: 'Beneficios para miembros' },
  { icon: CreditCard, label: 'Cobro automático seguro' },
] as const;

interface SubscriptionPublicHeroProps {
  organization: SubscriptionOrgTheme;
  slug: string;
  heroTitle: string;
  heroSubtitle: string;
  ctaLabel: string;
  showFixedPlans: boolean;
  lowestPrice: number | null;
  defaultCurrency: string;
  onCopyLink: () => void;
}

function renderHeroTitle(heroTitle: string, orgName: string) {
  if (!heroTitle.includes(orgName)) {
    return heroTitle;
  }
  const [before, after] = heroTitle.split(orgName);
  return (
    <>
      {before}
      <span style={{ color: 'var(--subs-accent)' }}>{orgName}</span>
      {after}
    </>
  );
}

export function SubscriptionPublicHero({
  organization,
  slug,
  heroTitle,
  heroSubtitle,
  ctaLabel,
  showFixedPlans,
  lowestPrice,
  defaultCurrency,
  onCopyLink,
}: SubscriptionPublicHeroProps) {
  const priceLabel =
    showFixedPlans && lowestPrice !== null
      ? new Intl.NumberFormat('es-CL', {
          style: 'currency',
          currency: defaultCurrency,
          maximumFractionDigits: 0,
        }).format(lowestPrice)
      : null;

  return (
    <section className="relative overflow-hidden border-b border-[color-mix(in_srgb,var(--subs-accent)_15%,transparent)]">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 85% 55% at 0% -15%, color-mix(in srgb, var(--subs-accent) 18%, transparent), transparent 52%),
            radial-gradient(ellipse 45% 35% at 100% 5%, color-mix(in srgb, var(--subs-accent-secondary) 12%, transparent), transparent 48%),
            linear-gradient(to bottom, hsl(var(--background)), hsl(var(--background)))
          `,
        }}
      />
      <div
        className="pointer-events-none absolute -right-24 top-8 h-56 w-56 rounded-full opacity-30 blur-3xl lg:right-[12%]"
        style={{ background: 'var(--subs-accent)' }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
        <div className="flex min-h-[min(58vh,520px)] flex-col justify-center gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-14">
          <div className="max-w-2xl space-y-7 animate-in fade-in slide-in-from-left-4 duration-700 lg:py-4">
            <div className="flex flex-wrap items-center gap-2.5">
              <Badge
                variant="outline"
                className="gap-1.5 border-[color-mix(in_srgb,var(--subs-accent)_35%,transparent)] text-[var(--subs-accent)]"
              >
                <Sparkles className="h-3 w-3" />
                Membresía
              </Badge>
              {priceLabel && <Badge variant="secondary">Desde {priceLabel}</Badge>}
            </div>

            <div className="flex items-start gap-5">
              {organization.logo_url ? (
                <img 
                  src={organization.logo_url} 
                  alt={organization.name} 
                  width={84}
                  height={84}
                  loading="lazy"
                  className="shrink-0 rounded-2xl border border-white/10 object-contain shadow-lg shadow-black/20" 
                />
              ) : (
                <div
                  className="flex h-[84px] w-[84px] shrink-0 items-center justify-center rounded-2xl border border-white/10 shadow-lg"
                  style={{
                    background: 'color-mix(in srgb, var(--subs-accent) 16%, transparent)',
                  }}
                >
                  <Heart className="h-9 w-9 fill-[var(--subs-accent)] text-[var(--subs-accent)]" />
                </div>
              )}
              <div className="min-w-0 pt-1">
                <p className="mb-2 text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
                  Suscripciones
                </p>
                <h1 className="font-[family-name:var(--font-outfit)] text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl lg:leading-[1.06]">
                  {renderHeroTitle(heroTitle, organization.name)}
                </h1>
              </div>
            </div>

            <p className="max-w-xl text-base leading-relaxed text-muted-foreground sm:text-lg sm:leading-8">
              {heroSubtitle}
            </p>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-white/8 bg-card/25 px-5 py-3.5 text-sm text-muted-foreground backdrop-blur-sm">
              <span className="inline-flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-[var(--subs-accent)]" />
                Mercado Pago
              </span>
              <span className="hidden h-3 w-px bg-border/60 sm:block" aria-hidden />
              <span className="inline-flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-[var(--subs-accent)]" />
                Cancela cuando quieras
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Button
                asChild
                size="lg"
                className="h-11 rounded-xl px-7 text-white hover:opacity-90"
                style={{ background: 'var(--subs-accent)' }}
              >
                <a href="#planes">{ctaLabel}</a>
              </Button>
              <Button variant="ghost" size="sm" asChild className="text-muted-foreground">
                <Link to={`/merch/${slug}`}>Ver merch oficial</Link>
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-2 border-[color-mix(in_srgb,var(--subs-accent)_25%,transparent)]"
                onClick={onCopyLink}
              >
                <Copy className="h-3.5 w-3.5" />
                Copiar enlace
              </Button>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-1 lg:hidden">
              {PERKS.map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/10 bg-background/30 px-3 py-1.5 text-xs text-foreground/85"
                >
                  <Icon className="h-3.5 w-3.5 text-[var(--subs-accent)]" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          <aside
            className="hidden w-full max-w-[320px] shrink-0 animate-in fade-in slide-in-from-right-4 duration-700 delay-100 lg:block lg:py-4"
            aria-label="Beneficios de membresía"
          >
            <div
              className="overflow-hidden rounded-2xl border border-white/10 shadow-xl shadow-black/15 lg:min-h-[280px]"
              style={{
                background: `linear-gradient(145deg, color-mix(in srgb, var(--subs-accent) 14%, transparent) 0%, hsl(var(--card)/0.45) 100%)`,
              }}
            >
              <div
                className="h-1 w-full"
                style={{
                  background: `linear-gradient(90deg, var(--subs-accent), color-mix(in srgb, var(--subs-accent-secondary, var(--subs-accent)) 70%, transparent))`,
                }}
              />
              <div className="space-y-1.5 px-5 py-5">
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
                  Al suscribirte
                </p>
                {priceLabel && (
                  <p className="font-[family-name:var(--font-outfit)] text-3xl font-bold tabular-nums">
                    {priceLabel}
                    <span className="text-sm font-normal text-muted-foreground"> / mes</span>
                  </p>
                )}
              </div>
              <ul className="space-y-2.5 border-t border-white/8 px-4 py-4">
                {PERKS.map(({ icon: Icon, label }) => (
                  <li
                    key={label}
                    className="flex items-center gap-3 rounded-lg border border-white/5 bg-background/25 px-3.5 py-3 backdrop-blur-sm"
                  >
                    <span
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg"
                      style={{
                        background: 'color-mix(in srgb, var(--subs-accent) 14%, transparent)',
                      }}
                    >
                      <Icon className="h-4 w-4 text-[var(--subs-accent)]" />
                    </span>
                    <span className="text-sm leading-snug">{label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
